import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, LogOut, Sparkles, Trash2, Pencil, ExternalLink, Download, QrCode, Image as ImageIcon, X, Layers, Check, Wand2, Film, Images, Mic, FileText, Coins } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { QRCodeCanvas } from "qrcode.react";
import MagicCatalogDialog from "@/components/app/MagicCatalogDialog";
import VideoCatalogDialog from "@/components/app/VideoCatalogDialog";
import BulkPhotoDialog from "@/components/app/BulkPhotoDialog";
import ReelMakerDialog from "@/components/app/ReelMakerDialog";
import CollectionReelDialog from "@/components/app/CollectionReelDialog";
import VoicePriceDialog from "@/components/app/VoicePriceDialog";
import StudioDialog from "@/components/app/StudioDialog";
import ShopSettingsDialog from "@/components/app/ShopSettingsDialog";
import BulkAIDialog from "@/components/app/BulkAIDialog";
import SareeSectionPicker from "@/components/app/SareeSectionPicker";
import AIGalleryDialog from "@/components/app/AIGalleryDialog";
import { PRODUCT_TYPES, PRODUCT_TYPE_LABEL, isJewellery } from "@/lib/productTypes";
import GoldRateDialog from "@/components/app/GoldRateDialog";

const OCCASIONS = ["bridal", "festive", "party", "casual", "office"];
const FABRICS = ["Kanjivaram Silk", "Banarasi Silk", "Cotton", "Linen", "Georgette", "Organza", "Chiffon", "Tussar Silk"];
const PURITIES = ["22K", "18K", "916", "14K", "925 Silver", "Silver"];

const empty = {
    title: "", brand: "", description: "", price: "", original_price: "",
    fabric: "", occasion: "", category: "", colors: "",
    images: [], matching_blouse: "", stock: 1, is_trending: false, is_new_arrival: true, offer: "",
    product_type: "",
    // Jewellery extras
    purity: "", gross_weight_g: "", net_weight_g: "", stone_weight_g: "",
    making_charges_pct: "", making_charges_inr: "", huid: "",
};

async function fileToDataURL(file, { maxDim = 1600, quality = 0.82, maxOutputBytes = 3_500_000 } = {}) {
    // Read the file into an image element, draw it onto a canvas at a sensible size,
    // then re-export as JPEG so phone photos (often 4-12MB) become ~150-400KB.
    // Falls back to the original data URL if the image API fails.
    const readRaw = () => new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
    try {
        const raw = await readRaw();
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = raw;
        });
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        let q = quality;
        let out = canvas.toDataURL("image/jpeg", q);
        // If still too big, step quality down
        while (out.length > maxOutputBytes && q > 0.4) {
            q -= 0.1;
            out = canvas.toDataURL("image/jpeg", q);
        }
        return out;
    } catch (e) {
        // Fallback: use the original file as data URL
        return await readRaw();
    }
}

export default function ShopAdmin() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [shop, setShop] = useState(null);
    const [sarees, setSarees] = useState([]);
    const [trials, setTrials] = useState([]);
    const [staffReqs, setStaffReqs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [aiBusy, setAiBusy] = useState({});
    const [aiSavings, setAiSavings] = useState(null);
    const [magicSaree, setMagicSaree] = useState(null);
    const [reelSaree, setReelSaree] = useState(null);
    const [gallerySaree, setGallerySaree] = useState(null);
    const [collectionOpen, setCollectionOpen] = useState(false);
    const [videoOpen, setVideoOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [voicePriceOpen, setVoicePriceOpen] = useState(false);
    const [studioOpen, setStudioOpen] = useState(false);
    const [goldRateOpen, setGoldRateOpen] = useState(false);
    const [newCatName, setNewCatName] = useState("");
    const [editingCatId, setEditingCatId] = useState(null);
    const [editCatName, setEditCatName] = useState("");
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const [s, sr, tr, st, ca, sav] = await Promise.all([
                api.get("/admin/me/shop"),
                api.get("/admin/sarees"),
                api.get("/admin/trial-bookings"),
                api.get("/admin/staff-requests"),
                api.get("/admin/categories"),
                api.get("/admin/ai-cache/stats").catch(() => ({ data: null })),
            ]);
            setShop(s.data); setSarees(sr.data); setTrials(tr.data); setStaffReqs(st.data); setCategories(ca.data);
            setAiSavings(sav.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const addCategory = async (e) => {
        e.preventDefault();
        const name = newCatName.trim();
        if (!name) return;
        try {
            const { data } = await api.post("/admin/categories", { name });
            setCategories((c) => [...c, data]);
            setNewCatName("");
            toast.success(`Row "${data.name}" added`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add row");
        }
    };

    const startEditCat = (c) => { setEditingCatId(c.id); setEditCatName(c.name); };

    const downloadOnboardingKit = async () => {
        try {
            const res = await api.get("/admin/onboarding-kit", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `vastra-onboarding-${shop?.slug || "shop"}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Onboarding kit downloaded");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
        }
    };

    const downloadSareeQrTags = async () => {
        try {
            const res = await api.get("/admin/saree-qr-tags", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `vastra-saree-tags-${shop?.slug || "shop"}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Saree QR tags downloaded — print on A4 and stick on hangers");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
        }
    };

    const downloadCategoryPosters = async () => {
        try {
            const res = await api.get("/admin/category-qr-posters", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `vastra-category-posters-${shop?.slug || "shop"}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Category posters downloaded — one A4 poster per category");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
        }
    };
    const saveEditCat = async (id) => {
        const name = editCatName.trim();
        if (!name) return;
        try {
            const { data } = await api.patch(`/admin/categories/${id}`, { name });
            setCategories((cats) => cats.map((c) => (c.id === id ? data : c)));
            setEditingCatId(null);
            toast.success("Row renamed");
            // also reload sarees in case category slugs changed
            const sr = await api.get("/admin/sarees");
            setSarees(sr.data);
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const removeCategory = async (id, name) => {
        if (!window.confirm(`Delete row "${name}"? Sarees in this row will become uncategorised.`)) return;
        try {
            await api.delete(`/admin/categories/${id}`);
            setCategories((c) => c.filter((x) => x.id !== id));
            toast.success("Row deleted");
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const moveCategory = async (idx, dir) => {
        const next = [...categories];
        const swap = idx + dir;
        if (swap < 0 || swap >= next.length) return;
        [next[idx], next[swap]] = [next[swap], next[idx]];
        setCategories(next);
        try { await api.post("/admin/categories/reorder", { ids: next.map((c) => c.id) }); }
        catch (err) { toast.error("Reorder failed"); load(); }
    };

    const openNew = () => {
        setEditing(null);
        // Pre-fill product_type with the shop's default so single-type shops feel one-tap
        setForm({ ...empty, product_type: shop?.default_product_type || "saree" });
        setOpen(true);
    };
    const openEdit = (s) => {
        setEditing(s);
        setForm({
            ...s,
            colors: (s.colors || []).join(", "),
            price: s.price ?? "",
            original_price: s.original_price ?? "",
            images: s.images || [],
        });
        setOpen(true);
    };

    const onPickFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const datas = await compressFiles(files);
        setForm((f) => ({ ...f, images: [...(f.images || []), ...datas].slice(0, 8) }));
        if (fileRef.current) fileRef.current.value = "";
    };

    // Reusable client-side image compressor + validator. Used by the legacy
    // flat picker AND by the new SareeSectionPicker (one per section).
    const compressFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        const imgs = files.filter((f) => f.type.startsWith("image/"));
        if (imgs.length < files.length) toast.warning("Only image files are supported");
        const oversized = imgs.filter((f) => f.size > 25 * 1024 * 1024);
        if (oversized.length) toast.warning(`${oversized.length} photo(s) over 25MB were skipped`);
        const ok = imgs.filter((f) => f.size <= 25 * 1024 * 1024);
        return Promise.all(ok.map((f) => fileToDataURL(f)));
    };

    const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                price: form.price === "" || form.price == null ? 0 : parseFloat(form.price),
                original_price: form.original_price ? parseFloat(form.original_price) : null,
                stock: parseInt(form.stock || 0, 10),
                colors: typeof form.colors === "string"
                    ? form.colors.split(",").map((s) => s.trim()).filter(Boolean)
                    : form.colors,
                // Coerce jewellery numeric fields — empty strings → null so backend Pydantic
                // doesn't reject them.
                gross_weight_g: form.gross_weight_g === "" ? null : parseFloat(form.gross_weight_g),
                net_weight_g: form.net_weight_g === "" ? null : parseFloat(form.net_weight_g),
                stone_weight_g: form.stone_weight_g === "" ? null : parseFloat(form.stone_weight_g),
                making_charges_pct: form.making_charges_pct === "" ? null : parseFloat(form.making_charges_pct),
                making_charges_inr: form.making_charges_inr === "" ? null : parseFloat(form.making_charges_inr),
                purity: form.purity || null,
                huid: form.huid || null,
            };
            if (editing) {
                await api.patch(`/admin/sarees/${editing.id}`, payload);
                toast.success("Item updated");
            } else {
                await api.post("/admin/sarees", payload);
                toast.success("Item added");
            }
            setOpen(false); setEditing(null); setForm(empty); load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this item?")) return;
        try { await api.delete(`/admin/sarees/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const generateAI = async (id, style = "modern", skin_tone = "medium") => {
        setAiBusy((b) => ({ ...b, [id]: true }));
        try {
            const { data } = await api.post(`/admin/sarees/${id}/ai-model`, { style, skin_tone });
            const jobId = data.job_id;
            toast.info("AI is creating your model photo… usually 15–90 seconds.", { duration: 6000 });
            // Poll for job completion (up to 5 min — Google can be slow when busy)
            const start = Date.now();
            while (Date.now() - start < 300000) {
                await new Promise((r) => setTimeout(r, 4000));
                try {
                    const { data: job } = await api.get(`/admin/ai-jobs/${jobId}`);
                    if (job.status === "done") {
                        const providerLabel = job.provider === "cache"
                            ? "from cache · ₹0"
                            : job.provider === "google-direct"
                                ? "your Google key"
                                : "universal key";
                        toast.success(`AI model image added (${providerLabel})`);
                        await load();
                        return;
                    }
                    if (job.status === "failed") {
                        toast.error(job.error || "AI generation failed");
                        return;
                    }
                } catch { /* keep polling */ }
            }
            toast.warning("Still generating in background — refresh in a minute to see the image.");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI generation failed");
        } finally { setAiBusy((b) => ({ ...b, [id]: false })); }
    };

    const doLogout = async () => { await logout(); nav("/login"); };

    const shopUrl = shop ? `${window.location.origin}/shop/${shop.slug}` : "";
    const downloadQR = () => {
        const c = document.querySelector("canvas[data-qr='shop']");
        if (!c) return;
        const url = c.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = url; a.download = `${shop.slug}-qr.png`; a.click();
    };

    return (
        <div className="min-h-screen bg-brand-beige">
            <header className="ayraa-glass sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex flex-col leading-tight min-w-0" data-testid="shopadmin-brand">
                        <span className="ayraa-overline text-[9px]">Vastra AI</span>
                        <span className="font-display text-xl sm:text-2xl tracking-editorial truncate">
                            {shop?.name || "Shop"}
                        </span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <ShopSettingsDialog />
                        <button className="ayraa-btn-ghost text-sm" onClick={doLogout} data-testid="logout-btn">
                            <LogOut className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
                {!loading && shop && (
                    <div className="grid lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            <div className="ayraa-overline">Shop owner</div>
                            <h1 className="font-serif text-4xl sm:text-5xl mt-2">{shop.name}</h1>
                            <p className="text-brand-muted mt-2">{shop.location || "—"} · {sarees.length} item{sarees.length === 1 ? "" : "s"} in catalog</p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <button className="ayraa-btn-primary" onClick={openNew} data-testid="add-saree-btn">
                                    <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add item
                                </button>
                                <button className="ayraa-btn-outline" onClick={() => setBulkOpen(true)} data-testid="bulk-photos-btn">
                                    <Images className="w-4 h-4 mr-2" strokeWidth={1.5} /> Bulk photos
                                </button>
                                <button className="ayraa-btn-outline" onClick={() => setVideoOpen(true)} data-testid="video-upload-btn">
                                    <Film className="w-4 h-4 mr-2" strokeWidth={1.5} /> Upload video
                                </button>
                                <button className="ayraa-btn-outline" onClick={() => setCollectionOpen(true)} data-testid="collection-reel-btn">
                                    <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} /> Collection reel
                                </button>
                                <button className="ayraa-btn-outline" onClick={() => setVoicePriceOpen(true)} data-testid="voice-price-btn">
                                    <Mic className="w-4 h-4 mr-2" strokeWidth={1.5} /> Voice price
                                </button>
                                <button className="ayraa-btn-outline" onClick={() => setStudioOpen(true)} data-testid="studio-btn">
                                    <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Studio
                                </button>
                                {/* Gold-rate tool — always visible to shop owners. Only jewellery
                                    shops will actually use it; saree shops can ignore it. */}
                                <button className="ayraa-btn-outline" onClick={() => setGoldRateOpen(true)} data-testid="gold-rate-btn">
                                    <Coins className="w-4 h-4 mr-2" strokeWidth={1.5} /> Today&apos;s gold rate
                                </button>
                                <BulkAIDialog onAfterDone={load} />
                                <button className="ayraa-btn-outline" onClick={downloadOnboardingKit} data-testid="onboarding-kit-btn">
                                    <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} /> Onboarding kit
                                </button>
                                <button className="ayraa-btn-outline" onClick={downloadSareeQrTags} data-testid="saree-qr-tags-btn">
                                    <QrCode className="w-4 h-4 mr-2" strokeWidth={1.5} /> Print saree QR tags
                                </button>
                                <button className="ayraa-btn-outline" onClick={downloadCategoryPosters} data-testid="category-posters-btn">
                                    <QrCode className="w-4 h-4 mr-2" strokeWidth={1.5} /> Category QR posters
                                </button>
                                <Link className="ayraa-btn-outline" to={`/shop/${shop.slug}`} target="_blank" data-testid="open-shop-link">
                                    <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} /> Open customer view
                                </Link>
                            </div>
                        </div>
                        <div className="ayraa-card p-5 flex items-center gap-4" data-testid="shop-qr-card">
                            <div className="bg-white p-2 rounded-md border border-brand-border">
                                <QRCodeCanvas value={shopUrl} size={120} bgColor="#ffffff" fgColor="#1F1A17" level="M" data-qr="shop" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="ayraa-overline flex items-center gap-1"><QrCode className="w-3 h-3" strokeWidth={1.5} /> Shop QR</div>
                                <div className="font-serif text-xl mt-1 truncate">{shop.slug}</div>
                                <div className="text-xs text-brand-muted truncate">{shopUrl}</div>
                                <button className="ayraa-btn-ghost text-xs mt-2 -ml-2" onClick={downloadQR} data-testid="qr-download-btn">
                                    <Download className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Download PNG
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {aiSavings && aiSavings.entries > 0 ? (
                    <div className="mt-6 ayraa-card p-4 flex items-center gap-4" data-testid="ai-savings-card">
                        <div className="w-10 h-10 grid place-items-center rounded-md bg-brand-subtle text-brand-primary">
                            <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1">
                            <div className="ayraa-overline">AI cache</div>
                            <div className="font-serif text-xl mt-0.5">
                                {aiSavings.total_hits} cached re-use{aiSavings.total_hits === 1 ? "" : "s"}
                            </div>
                            <div className="text-xs text-brand-muted">{aiSavings.entries} unique source-style combo{aiSavings.entries === 1 ? "" : "s"} cached</div>
                        </div>
                    </div>
                ) : null}

                <div className="ayraa-divider my-10" />

                <Tabs defaultValue="sarees" className="w-full">
                    <div className="overflow-x-auto scroll-x -mx-5 px-5 sm:-mx-8 sm:px-8">
                        <TabsList className="bg-brand-subtle inline-flex w-max">
                            <TabsTrigger value="sarees" data-testid="tab-sarees">Catalog ({sarees.length})</TabsTrigger>
                            <TabsTrigger value="rows" data-testid="tab-rows">Catalog rows ({categories.length})</TabsTrigger>
                            <TabsTrigger value="trials" data-testid="tab-trials">Trial bookings ({trials.length})</TabsTrigger>
                            <TabsTrigger value="staff" data-testid="tab-staff">Staff requests ({staffReqs.length})</TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="sarees" className="mt-6">
                        {sarees.length === 0 ? (
                            <div className="ayraa-card p-10 text-center">
                                <ImageIcon className="w-10 h-10 mx-auto text-brand-gold" strokeWidth={1.2} />
                                <div className="font-serif text-2xl mt-4">No items yet</div>
                                <p className="text-brand-muted mt-2">Add your first item to power the customer catalog.</p>
                                <button className="ayraa-btn-primary mt-5" onClick={openNew}>Add first item</button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5" data-testid="admin-sarees-grid">
                                {sarees.map((s) => {
                                    const cover = s.ai_model_images?.[0] || s.images?.[0];
                                    return (
                                    <div key={s.id} className="ayraa-card flex flex-col" data-testid={`saree-card-${s.id}`}>
                                        <div className="aspect-[3/4] bg-brand-subtle relative overflow-hidden">
                                            {cover ? <img src={cover} alt={s.title} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : null}
                                            {s.is_trending ? <span className="absolute top-3 left-3 ayraa-overline bg-white/90 px-2 py-1 rounded">Trending</span> : null}
                                            {s.ai_model_images?.length ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setGallerySaree(s)}
                                                    className="absolute top-3 right-3 bg-brand-ink/85 hover:bg-brand-ink text-white text-[10px] font-mono uppercase tracking-wider-2 px-2 py-1 rounded transition"
                                                    title="View AI photos"
                                                    data-testid={`saree-view-ai-${s.id}`}
                                                >Live · {s.ai_model_images.length} AI</button>
                                            ) : (
                                                <span className="absolute top-3 right-3 bg-brand-ink/85 text-brand-goldlight text-[10px] font-mono uppercase tracking-wider-2 px-2 py-1 rounded">Hidden</span>
                                            )}
                                        </div>
                                        <div className="p-4 flex-1 flex flex-col">
                                            <div className="ayraa-overline">{PRODUCT_TYPE_LABEL[s.product_type] || s.fabric || "Item"}</div>
                                            <div className="font-serif text-lg mt-1 line-clamp-1">{s.title}</div>
                                            <div className="mt-1 text-brand-primary font-medium">₹{Number(s.price).toLocaleString("en-IN")}</div>
                                            <div className="text-xs text-brand-muted mt-1">Stock: {s.stock}</div>
                                            <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-4">
                                                <button className="ayraa-btn-ghost text-xs px-2.5 py-1.5" onClick={() => openEdit(s)} data-testid={`saree-edit-${s.id}`}>
                                                    <Pencil className="w-3.5 h-3.5 sm:mr-1" strokeWidth={1.5} /> <span className="hidden sm:inline">Edit</span>
                                                </button>
                                                <button
                                                    className="ayraa-btn-ghost text-xs px-2.5 py-1.5 text-brand-primary"
                                                    onClick={() => setMagicSaree(s)}
                                                    disabled={!s.images?.length}
                                                    data-testid={`saree-magic-${s.id}`}
                                                >
                                                    <Wand2 className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> <span className="sm:hidden">Magic</span><span className="hidden sm:inline">Magic Catalog</span>
                                                </button>
                                                <button
                                                    className="ayraa-btn-ghost text-xs px-2.5 py-1.5 text-brand-primary"
                                                    onClick={() => setReelSaree(s)}
                                                    disabled={!s.ai_model_images?.length}
                                                    title={!s.ai_model_images?.length ? "Generate AI model photo first" : "Make Instagram Reel"}
                                                    data-testid={`saree-reel-${s.id}`}
                                                >
                                                    <Film className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> <span className="sm:hidden">Reel</span><span className="hidden sm:inline">Make Reel</span>
                                                </button>
                                                <button className="ayraa-btn-ghost text-xs px-2.5 py-1.5 text-brand-primary ml-auto" onClick={() => remove(s.id)} data-testid={`saree-delete-${s.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="rows" className="mt-6">
                        <div className="grid lg:grid-cols-3 gap-6">
                            <div className="ayraa-card p-5 lg:col-span-1">
                                <div className="ayraa-overline flex items-center gap-1.5"><Layers className="w-3 h-3" strokeWidth={1.5} /> Add a row</div>
                                <p className="text-sm text-brand-muted mt-2">
                                    Rows are how customers browse your shop — Pattu Sarees, Cotton, Row 9, Row 10… anything you want.
                                </p>
                                <form onSubmit={addCategory} className="mt-4 space-y-2" data-testid="add-category-form">
                                    <input
                                        className="ayraa-input"
                                        placeholder='e.g. "Pattu Sarees"'
                                        value={newCatName}
                                        onChange={(e) => setNewCatName(e.target.value)}
                                        data-testid="category-name-input"
                                    />
                                    <button type="submit" className="ayraa-btn-primary w-full" data-testid="add-category-btn">
                                        <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Create row
                                    </button>
                                </form>
                            </div>

                            <div className="lg:col-span-2">
                                {categories.length === 0 ? (
                                    <div className="ayraa-card p-10 text-center">
                                        <Layers className="w-9 h-9 mx-auto text-brand-gold" strokeWidth={1.2} />
                                        <div className="font-serif text-2xl mt-4">No catalog rows yet</div>
                                        <p className="text-brand-muted mt-2">Add your first row — it appears as a quick chip in the customer catalog.</p>
                                    </div>
                                ) : (
                                    <div className="ayraa-card divide-y divide-brand-border" data-testid="categories-list">
                                        {categories.map((c, i) => (
                                            <div key={c.id} className="p-4 flex items-center gap-3" data-testid={`category-row-${c.slug}`}>
                                                <span className="ayraa-overline w-8 text-center text-brand-muted">{String(i + 1).padStart(2, "0")}</span>
                                                {editingCatId === c.id ? (
                                                    <>
                                                        <input
                                                            className="ayraa-input flex-1"
                                                            value={editCatName}
                                                            onChange={(e) => setEditCatName(e.target.value)}
                                                            autoFocus
                                                        />
                                                        <button className="ayraa-btn-ghost text-xs px-2 py-1 text-brand-primary" onClick={() => saveEditCat(c.id)} data-testid={`category-save-${c.slug}`}>
                                                            <Check className="w-4 h-4" strokeWidth={1.5} />
                                                        </button>
                                                        <button className="ayraa-btn-ghost text-xs px-2 py-1" onClick={() => setEditingCatId(null)}>
                                                            <X className="w-4 h-4" strokeWidth={1.5} />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-serif text-lg leading-tight">{c.name}</div>
                                                            <div className="text-xs text-brand-muted mt-0.5">/{c.slug} · {c.saree_count} saree{c.saree_count === 1 ? "" : "s"}</div>
                                                        </div>
                                                        <button className="ayraa-btn-ghost text-xs px-2 py-1" onClick={() => moveCategory(i, -1)} disabled={i === 0} aria-label="up">↑</button>
                                                        <button className="ayraa-btn-ghost text-xs px-2 py-1" onClick={() => moveCategory(i, 1)} disabled={i === categories.length - 1} aria-label="down">↓</button>
                                                        <button className="ayraa-btn-ghost text-xs px-2 py-1.5" onClick={() => startEditCat(c)} data-testid={`category-edit-${c.slug}`}>
                                                            <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                        </button>
                                                        <button className="ayraa-btn-ghost text-xs px-2 py-1.5 text-brand-primary" onClick={() => removeCategory(c.id, c.name)} data-testid={`category-delete-${c.slug}`}>
                                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="trials" className="mt-6">                        {trials.length === 0 ? <EmptyMsg label="No trial bookings yet." /> : (
                            <div className="ayraa-card overflow-x-auto">
                                <table className="w-full text-sm min-w-[640px]">
                                    <thead className="bg-brand-subtle text-left"><tr>
                                        <th className="p-3">Customer</th><th className="p-3">Phone</th><th className="p-3">Date</th><th className="p-3">Saree</th><th className="p-3">Note</th>
                                    </tr></thead>
                                    <tbody>
                                        {trials.map((t) => (
                                            <tr key={t.id} className="border-t border-brand-border">
                                                <td className="p-3 whitespace-nowrap">{t.customer_name}</td>
                                                <td className="p-3 whitespace-nowrap">{t.customer_phone}</td>
                                                <td className="p-3 whitespace-nowrap">{t.preferred_date}</td>
                                                <td className="p-3 text-brand-muted whitespace-nowrap">{t.saree_id?.slice(0, 8)}…</td>
                                                <td className="p-3 text-brand-muted">{t.note || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="staff" className="mt-6">
                        {staffReqs.length === 0 ? <EmptyMsg label="No staff assistance requests." /> : (
                            <div className="ayraa-card overflow-x-auto">
                                <table className="w-full text-sm min-w-[560px]">
                                    <thead className="bg-brand-subtle text-left"><tr>
                                        <th className="p-3">Customer</th><th className="p-3">Phone</th><th className="p-3">Saree</th><th className="p-3">Message</th>
                                    </tr></thead>
                                    <tbody>
                                        {staffReqs.map((t) => (
                                            <tr key={t.id} className="border-t border-brand-border">
                                                <td className="p-3 whitespace-nowrap">{t.customer_name}</td>
                                                <td className="p-3 whitespace-nowrap">{t.customer_phone}</td>
                                                <td className="p-3 text-brand-muted whitespace-nowrap">{t.saree_id ? `${t.saree_id.slice(0, 8)}…` : "—"}</td>
                                                <td className="p-3 text-brand-muted">{t.message || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </main>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-brand-paper border-brand-border max-w-xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit item" : "Add item"}</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-5" data-testid="saree-form">
                        {/* Photos block — guided 4-section picker for sarees,
                            flat picker for jewellery/kurti/etc. */}
                        {form.product_type === "saree" ? (
                            <SareeSectionPicker
                                initialImages={editing ? (editing.images || []) : []}
                                initialSections={editing ? (editing.image_sections || []) : []}
                                compressFiles={compressFiles}
                                onChange={({ images, image_sections }) =>
                                    setForm((f) => ({ ...f, images, image_sections }))
                                }
                            />
                        ) : (
                            <div>
                                <label className="ayraa-overline">Item photos <span className="text-brand-bronze">*</span></label>
                                <div className="mt-2 grid grid-cols-4 gap-2">
                                    {form.images.map((src, i) => (
                                        <div key={i} className="relative aspect-square rounded-sm overflow-hidden border border-brand-border">
                                            <img src={src} alt={`up-${i}`} className="w-full h-full object-cover" />
                                            <button type="button" className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5" onClick={() => removeImage(i)}>
                                                <X className="w-3 h-3" strokeWidth={2} />
                                            </button>
                                        </div>
                                    ))}
                                    <label className="aspect-square rounded-sm border border-dashed border-brand-border grid place-items-center cursor-pointer text-brand-muted text-xs hover:bg-brand-subtle">
                                        <input type="file" accept="image/*" multiple ref={fileRef} className="hidden" onChange={onPickFiles} data-testid="saree-images-input" />
                                        + Photo
                                    </label>
                                </div>
                                <p className="text-xs text-brand-soft mt-1.5">Tap to select from your gallery · any size, we&apos;ll auto-compress.</p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="ayraa-overline">Product type <span className="text-brand-primary">*</span></label>
                                <select
                                    className="ayraa-input mt-1.5"
                                    required
                                    value={form.product_type || ""}
                                    onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                                    data-testid="saree-product-type-select"
                                >
                                    <option value="">— Pick a type —</option>
                                    {PRODUCT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="ayraa-overline">Catalog row <span className="text-brand-soft text-[10px] tracking-normal">(optional)</span></label>
                                <select className="ayraa-input mt-1.5" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="saree-category-select">
                                    <option value="">— None —</option>
                                    {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <details className="ayraa-card p-4 group">
                            <summary className="cursor-pointer list-none flex items-center justify-between text-sm font-medium" data-testid="saree-more-details-toggle">
                                <span>More details <span className="text-brand-muted font-normal">(optional)</span></span>
                                <span className="ayraa-overline text-brand-gold group-open:hidden">Show</span>
                                <span className="ayraa-overline text-brand-gold hidden group-open:inline">Hide</span>
                            </summary>
                            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-brand-border">
                                <div className="col-span-2">
                                    <label className="ayraa-overline">Title</label>
                                    <input className="ayraa-input mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={`Auto: ${PRODUCT_TYPE_LABEL[form.product_type] || "Item"} #${sarees.length + 1}`} data-testid="saree-title-input" />
                                </div>
                                <div>
                                    <label className="ayraa-overline">Price (₹)</label>
                                    <input type="number" min="0" className="ayraa-input mt-1.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="saree-price-input" placeholder={isJewellery(form.product_type) ? "auto-calc from weight" : "leave empty for now"} />
                                </div>
                                <div>
                                    <label className="ayraa-overline">Brand</label>
                                    <input className="ayraa-input mt-1.5" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                                </div>
                                <div>
                                    <label className="ayraa-overline">Stock</label>
                                    <input type="number" min="0" className="ayraa-input mt-1.5" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                                </div>
                                <div>
                                    <label className="ayraa-overline">Original price</label>
                                    <input type="number" min="0" className="ayraa-input mt-1.5" value={form.original_price || ""} onChange={(e) => setForm({ ...form, original_price: e.target.value })} placeholder="for strike-through" />
                                </div>
                                <div>
                                    <label className="ayraa-overline">Offer</label>
                                    <input className="ayraa-input mt-1.5" value={form.offer || ""} onChange={(e) => setForm({ ...form, offer: e.target.value })} placeholder="10% off" />
                                </div>

                                {isJewellery(form.product_type) ? (
                                    <>
                                        <div className="col-span-2 pt-2 border-t border-brand-border mt-2">
                                            <div className="ayraa-overline text-brand-bronze">Jewellery details (BIS-friendly)</div>
                                        </div>
                                        <div>
                                            <label className="ayraa-overline">Purity</label>
                                            <select className="ayraa-input mt-1.5" value={form.purity || ""} onChange={(e) => setForm({ ...form, purity: e.target.value })} data-testid="jw-purity-select">
                                                <option value="">—</option>
                                                {PURITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="ayraa-overline">HUID / Hallmark</label>
                                            <input className="ayraa-input mt-1.5" value={form.huid || ""} onChange={(e) => setForm({ ...form, huid: e.target.value })} placeholder="6-char BIS HUID" data-testid="jw-huid-input" />
                                        </div>
                                        <div>
                                            <label className="ayraa-overline">Gross weight (g)</label>
                                            <input type="number" step="0.01" min="0" className="ayraa-input mt-1.5" value={form.gross_weight_g} onChange={(e) => setForm({ ...form, gross_weight_g: e.target.value })} data-testid="jw-gross-weight-input" />
                                        </div>
                                        <div>
                                            <label className="ayraa-overline">Net (gold) weight (g)</label>
                                            <input type="number" step="0.01" min="0" className="ayraa-input mt-1.5" value={form.net_weight_g} onChange={(e) => setForm({ ...form, net_weight_g: e.target.value })} data-testid="jw-net-weight-input" />
                                        </div>
                                        <div>
                                            <label className="ayraa-overline">Stone weight (g)</label>
                                            <input type="number" step="0.01" min="0" className="ayraa-input mt-1.5" value={form.stone_weight_g} onChange={(e) => setForm({ ...form, stone_weight_g: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="ayraa-overline">Making (%)</label>
                                            <input type="number" step="0.5" min="0" className="ayraa-input mt-1.5" value={form.making_charges_pct} onChange={(e) => setForm({ ...form, making_charges_pct: e.target.value })} placeholder="e.g. 12" data-testid="jw-making-pct-input" />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="ayraa-overline">OR Making charges (₹ flat)</label>
                                            <input type="number" min="0" className="ayraa-input mt-1.5" value={form.making_charges_inr} onChange={(e) => setForm({ ...form, making_charges_inr: e.target.value })} placeholder="overrides % if filled" />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div>
                                            <label className="ayraa-overline">Fabric</label>
                                            <select className="ayraa-input mt-1.5" value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })}>
                                                <option value="">—</option>
                                                {FABRICS.map((f) => <option key={f} value={f}>{f}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="ayraa-overline">Occasion</label>
                                            <select className="ayraa-input mt-1.5" value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })}>
                                                <option value="">—</option>
                                                {OCCASIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                                            </select>
                                        </div>
                                    </>
                                )}
                                <div className="col-span-2">
                                    <label className="ayraa-overline">Colors (comma separated)</label>
                                    <input className="ayraa-input mt-1.5" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder={isJewellery(form.product_type) ? "Yellow gold, Diamond" : "Maroon, Gold"} />
                                </div>
                                <div className="col-span-2">
                                    <label className="ayraa-overline">Description</label>
                                    <textarea className="ayraa-input mt-1.5 min-h-[60px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                                </div>
                                {isJewellery(form.product_type) ? null : (
                                    <div className="col-span-2">
                                        <label className="ayraa-overline">Matching blouse note</label>
                                        <input className="ayraa-input mt-1.5" value={form.matching_blouse} onChange={(e) => setForm({ ...form, matching_blouse: e.target.value })} />
                                    </div>
                                )}
                                <div className="col-span-2 flex items-center gap-6 pt-2">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="checkbox" checked={!!form.is_trending} onChange={(e) => setForm({ ...form, is_trending: e.target.checked })} /> Trending
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="checkbox" checked={!!form.is_new_arrival} onChange={(e) => setForm({ ...form, is_new_arrival: e.target.checked })} /> New arrival
                                    </label>
                                </div>
                            </div>
                        </details>

                        <DialogFooter className="pt-1">
                            <button type="submit" className="ayraa-btn-primary w-full" disabled={saving || !form.images.length} data-testid="saree-save-btn">
                                {saving ? "Saving…" : (editing ? "Update item" : "Add item")}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <MagicCatalogDialog
                saree={magicSaree}
                open={!!magicSaree}
                onOpenChange={(o) => !o && setMagicSaree(null)}
                onDone={load}
            />

            <VideoCatalogDialog
                open={videoOpen}
                onOpenChange={setVideoOpen}
                onDone={load}
                defaultProductType={shop?.default_product_type || "saree"}
                categories={categories}
            />

            <BulkPhotoDialog
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                onDone={load}
                defaultProductType={shop?.default_product_type || "saree"}
                categories={categories}
            />

            <ReelMakerDialog
                saree={reelSaree}
                open={!!reelSaree}
                onOpenChange={(o) => !o && setReelSaree(null)}
            />

            <AIGalleryDialog
                saree={gallerySaree}
                open={!!gallerySaree}
                onOpenChange={(o) => !o && setGallerySaree(null)}
                onChange={(next) => {
                    setGallerySaree((g) => g ? { ...g, ai_model_images: next } : g);
                    load();
                }}
            />

            <CollectionReelDialog
                open={collectionOpen}
                onOpenChange={setCollectionOpen}
                sarees={sarees}
            />

            <VoicePriceDialog
                open={voicePriceOpen}
                onOpenChange={setVoicePriceOpen}
                sarees={sarees}
                onDone={load}
            />

            <StudioDialog
                open={studioOpen}
                onOpenChange={setStudioOpen}
                sarees={sarees}
                shop={shop}
                onShopUpdate={setShop}
                onDone={load}
            />

            <GoldRateDialog
                open={goldRateOpen}
                onOpenChange={setGoldRateOpen}
                current={shop}
                onSaved={(d) => setShop((s) => ({ ...s, ...d }))}
            />
        </div>
    );
}

function EmptyMsg({ label }) {
    return <div className="ayraa-card p-10 text-center text-brand-muted">{label}</div>;
}
